/**
 * LifeJourneyTimeline — Apple-style horizontal timeline connecting a user's
 * PAST ← NOW → FUTURE.
 *
 * Data sources (all existing):
 *   • Past   — GET /api/profile → profile.life_events (year, title, description)
 *            + POST / DELETE /api/profile/life-events
 *   • NOW    — synthesised from user profile + today's date
 *   • Future — GET /api/vedic/topics (if authed and chart built) — top 2
 *             upcoming windows per topic. Max 3 cards.
 *
 * This component is deliberately self-contained — no new backend. The
 * +Add Life Event dialog is inline. Future cards show short (<= 15 word)
 * predictions driven by the existing engine.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit3 } from "lucide-react";
import { api } from "@/hooks/useAuth";

const INDIGO = "#5D00FF";
const INK    = "#1C1C1C";
const MUTE   = "#676773";

function fmtYear(iso) {
  if (!iso) return "";
  const s = String(iso);
  const m = s.match(/^(\d{4})/);
  return m ? m[1] : s;
}

export default function LifeJourneyTimeline({ user }) {
  const [events, setEvents] = useState([]);
  const [future, setFuture] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ year: "", title: "", description: "" });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [prof, topics] = await Promise.allSettled([
          api.get("/profile"),
          api.get("/vedic/topics"),
        ]);
        if (!alive) return;
        if (prof.status === "fulfilled") {
          const le = prof.value?.data?.life_events || [];
          setEvents(le.sort((a, b) => String(a.year || "").localeCompare(String(b.year || ""))));
        }
        if (topics.status === "fulfilled") {
          const raw = topics.value?.data?.topics;
          // Normalise: backend returns an object keyed by topic name OR an array.
          const t = Array.isArray(raw)
            ? raw
            : (raw && typeof raw === "object"
                ? Object.entries(raw).map(([name, v]) => ({
                    topic: name,
                    name,
                    // common window-ish fields on the per-topic payload
                    windows: v?.windows || v?.next_5y || v?.upcoming || [],
                  }))
                : []);
          // Pick the top 3 upcoming windows across all topics, dedupe by year.
          const upcoming = [];
          t.forEach((tp) => {
            (tp.windows || []).slice(0, 2).forEach((w) => {
              const sd = w.start_date || w.start;
              if (!sd) return;
              const y = Number(fmtYear(sd));
              if (!y || y < new Date().getFullYear()) return;
              upcoming.push({
                year: y, topic: tp.topic || tp.name,
                blurb: (w.one_liner || w.note || "").split(" ").slice(0, 15).join(" "),
              });
            });
          });
          upcoming.sort((a, b) => a.year - b.year);
          const dedup = [];
          const seen = new Set();
          for (const u of upcoming) {
            const k = `${u.year}-${u.topic}`;
            if (seen.has(k)) continue;
            seen.add(k);
            dedup.push(u);
            if (dedup.length >= 3) break;
          }
          setFuture(dedup);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const nowLabel = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  }, []);

  const saveEvent = async () => {
    if (!draft.year || !draft.title) return;
    const row = { year: draft.year, title: draft.title.slice(0, 80), description: (draft.description || "").slice(0, 240) };
    try {
      if (editId) {
        // existing endpoint pattern: POST acts as upsert
        await api.post("/profile/life-events", { ...row, id: editId });
      } else {
        await api.post("/profile/life-events", row);
      }
      const { data } = await api.get("/profile");
      setEvents((data.life_events || []).sort((a, b) => String(a.year || "").localeCompare(String(b.year || ""))));
      setAdding(false); setEditId(null);
      setDraft({ year: "", title: "", description: "" });
    } catch (e) {
      console.warn("[LifeJourney] save failed", e?.message);
    }
  };

  const removeEvent = async (id) => {
    try {
      await api.delete(`/profile/life-events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (e) { console.warn("[LifeJourney] delete failed", e?.message); }
  };

  const now = new Date().getFullYear();
  const nowPicStyle = user?.picture
    ? { background: `url(${user.picture}) center/cover` }
    : { background: "linear-gradient(135deg,#5D00FF,#FF2E9A)" };

  return (
    <div data-testid="life-journey-timeline" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A1A1AA]">PAST ← NOW → FUTURE</div>
          <div className="font-serif text-[22px] text-[#1C1C1C] mt-1">Life Journey</div>
        </div>
        <button
          data-testid="life-journey-add-event"
          onClick={() => { setAdding(true); setDraft({ year: String(now - 1), title: "", description: "" }); }}
          className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border border-[#5D00FF]/25 text-[#5D00FF] hover:bg-[#5D00FF]/5"
        >
          <Plus size={14} /> Add life event
        </button>
      </div>

      {/* Horizontal scrollable rail */}
      <div
        className="overflow-x-auto pb-4 -mx-2 px-2"
        style={{ scrollbarWidth: "thin" }}
      >
        <div className="flex items-stretch gap-3 min-w-max">
          {loading && <div className="text-[12px] text-[#676773]">Loading your journey…</div>}

          {/* PAST */}
          {events.map((e) => (
            <div key={e.id || `${e.year}-${e.title}`}
                 data-testid={`past-event-${e.id || e.year}`}
                 className="w-[220px] rounded-2xl border border-black/5 bg-white/70 px-4 py-3 flex flex-col"
                 style={{ boxShadow: "0 6px 18px rgba(20,10,60,0.05)" }}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">{fmtYear(e.year)}</div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ opacity: 0.7 }}>
                  <button data-testid={`past-event-edit-${e.id || e.year}`}
                          onClick={() => { setEditId(e.id); setDraft({ year: e.year, title: e.title || "", description: e.description || "" }); setAdding(true); }}
                          className="text-[#676773] hover:text-[#5D00FF]"><Edit3 size={13}/></button>
                  <button data-testid={`past-event-del-${e.id || e.year}`}
                          onClick={() => e.id && removeEvent(e.id)}
                          className="text-[#676773] hover:text-red-500"><Trash2 size={13}/></button>
                </div>
              </div>
              <div className="text-[13px] font-medium text-[#1C1C1C] mt-1">{e.title || "Life moment"}</div>
              {e.description && <div className="text-[12px] text-[#676773] mt-1 leading-relaxed">{e.description}</div>}
            </div>
          ))}

          {/* NOW (always centered, biggest) */}
          <div
            data-testid="life-journey-now"
            className="w-[240px] rounded-2xl px-4 py-4 flex flex-col items-center text-center flex-shrink-0"
            style={{
              background: "linear-gradient(180deg, rgba(93,0,255,0.08), rgba(255,46,154,0.05))",
              border: "1px solid rgba(93,0,255,0.25)",
              boxShadow: "0 16px 36px rgba(93,0,255,0.15)",
            }}
          >
            <span className="w-14 h-14 rounded-full overflow-hidden mb-2" style={{ ...nowPicStyle, border: "2px solid rgba(255,255,255,0.9)" }} aria-hidden />
            <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: INDIGO }}>NOW</div>
            <div className="text-[13px] text-[#1C1C1C] mt-1 font-medium">{nowLabel}</div>
            <div className="text-[12px] text-[#676773] mt-1 leading-relaxed">
              Your journey continues — this is the only moment that is truly yours.
            </div>
          </div>

          {/* FUTURE */}
          {future.length === 0 && !loading && (
            <div className="w-[220px] rounded-2xl border border-dashed border-[#5D00FF]/30 bg-white/40 px-4 py-3 flex flex-col justify-center text-center">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">Future</div>
              <div className="text-[12px] text-[#676773] mt-2 leading-relaxed">
                Your Vedic timing will appear here as your chart engine warms up.
              </div>
            </div>
          )}
          {future.map((f, i) => (
            <div key={i} data-testid={`future-event-${f.year}`}
                 className="w-[220px] rounded-2xl border border-black/5 bg-white/70 px-4 py-3 flex flex-col"
                 style={{ boxShadow: "0 6px 18px rgba(20,10,60,0.05)" }}>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">{f.year} · {f.topic}</div>
              <div className="text-[13px] text-[#1C1C1C] mt-1 leading-snug">{f.blurb}</div>
              <div className="text-[10px] text-[#A1A1AA] mt-2 italic">A possibility · not a certainty</div>
            </div>
          ))}
        </div>
      </div>

      {/* Add/edit dialog */}
      {adding && (
        <div data-testid="life-journey-dialog" className="rounded-2xl border border-black/10 bg-white/90 p-4 space-y-2">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#676773]">
            {editId ? "Edit" : "Add"} a life moment
          </div>
          <input
            data-testid="life-journey-year"
            placeholder="Year (e.g. 2017)"
            value={draft.year}
            onChange={(e) => setDraft((d) => ({ ...d, year: e.target.value }))}
            className="w-full border border-black/10 rounded-xl px-3 py-2 text-[13px]"
          />
          <input
            data-testid="life-journey-title"
            placeholder="What happened? (short title)"
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            className="w-full border border-black/10 rounded-xl px-3 py-2 text-[13px]"
          />
          <textarea
            data-testid="life-journey-desc"
            placeholder="A sentence or two (optional)"
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            rows={2}
            className="w-full border border-black/10 rounded-xl px-3 py-2 text-[13px] resize-none"
          />
          <div className="flex gap-2 justify-end">
            <button data-testid="life-journey-cancel"
                    onClick={() => { setAdding(false); setEditId(null); setDraft({ year: "", title: "", description: "" }); }}
                    className="text-[12px] px-4 py-2 rounded-full border border-black/10 text-[#676773] hover:text-[#1C1C1C]">
              Cancel
            </button>
            <button data-testid="life-journey-save"
                    disabled={!draft.year || !draft.title}
                    onClick={saveEvent}
                    style={{ opacity: !draft.year || !draft.title ? 0.5 : 1 }}
                    className="text-[12px] px-4 py-2 rounded-full text-white"
                    >
              <span style={{ display: "inline-block", padding: "6px 14px", borderRadius: 999, background: `linear-gradient(135deg,${INDIGO},#FF2E9A)` }}>
                {editId ? "Save changes" : "Add to journey"}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
